import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { GroupIcon } from '@components';
import { DemographicData, BadgeDismissButton, BadgeTooltip, BaseBadgeWrapper } from './badge-parts';

interface SimilarCreatorsBadgeProps {
  data: DemographicData;
  onDismiss: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDismissItem?: (itemName: string) => void;
}

type CombinedProps = SimilarCreatorsBadgeProps & BadgeProps;

const SimilarCreatorsBadge = React.forwardRef<HTMLDivElement, CombinedProps>(
  ({ data, onDismiss, onDismissItem, ...props }, ref) => {
    const formatUsernames = (names: string[]) => names.join(', ');

    // If there's only one creator, render a single badge
    if (data.names.length === 1) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <BaseBadgeWrapper
                  ref={ref}
                  id={data.id}
                  actionName="open-similar-creators-filters"
                  {...props}
                >
                  <GroupIcon size={14} fill="#55A2DF" className="mr-2" />
                  Similar to:<span className="capitalize ml-1">{data.names[0]}</span>
                  <BadgeDismissButton
                    onDismiss={(e) => {
                      e.stopPropagation();
                      onDismiss(e);
                    }}
                    label="Similar Creators"
                    dismissActionName="remove-similar-creators-filters"
                  />
                </BaseBadgeWrapper>
              </div>
            </TooltipTrigger>
            <BadgeTooltip
              tooltipTitle="Similar Creators Filter"
              label="Similar to"
              names={data.names}
              formatNames={formatUsernames}
            />
          </Tooltip>
        </TooltipProvider>
      );
    }

    // For multiple creators, render individual badges
    return (
      <TooltipProvider>
        {data.names.map((creatorName, index) => (
          <Tooltip key={`${data.id}-${creatorName}-${index}`}>
            <TooltipTrigger asChild>
              <div className="inline-block">
                <BaseBadgeWrapper
                  ref={index === 0 ? ref : undefined}
                  id={`${data.id}-${creatorName}`}
                  actionName="open-similar-creators-filters"
                  {...props}
                >
                  <GroupIcon size={14} fill="#55A2DF" className="mr-2" />
                  Similar to:<span className="capitalize ml-1">{creatorName}</span>
                  <BadgeDismissButton
                    onDismiss={(e) => {
                      e.stopPropagation();
                      if (onDismissItem) {
                        onDismissItem(creatorName);
                      } else {
                        onDismiss(e);
                      }
                    }}
                    label={`Similar to ${creatorName}`}
                    dismissActionName="remove-similar-creator-filter"
                  />
                </BaseBadgeWrapper>
              </div>
            </TooltipTrigger>
            <BadgeTooltip
              tooltipTitle="Similar Creator Filter"
              label="Similar to"
              names={[creatorName]}
              formatNames={(names) => names[0]}
            />
          </Tooltip>
        ))}
      </TooltipProvider>
    );
  }
);

SimilarCreatorsBadge.displayName = 'SimilarCreatorsBadge';

export default SimilarCreatorsBadge;
